/**
 * AnalyticsStorageService
 *
 * Manages all localStorage read/write operations for analytics.
 * Implements ring-buffer eviction, size-guard, and schema migration.
 *
 * Design goals:
 *  • Never block the UI thread — writes are batched via microtask.
 *  • Graceful degradation: if storage is full or unavailable, events
 *    are kept in-memory and synced on next opportunity.
 *  • Schema versioning allows forward-compatible migrations.
 */
import { Injectable, OnDestroy } from '@angular/core';
import {
    ANALYTICS_STORAGE_KEY,
    ANALYTICS_VERSION,
    DEFAULT_ANALYTICS_CONFIG,
} from '../constants/analytics.constants';
import {
    AnalyticsConfig,
    AnalyticsEvent,
    AnalyticsStorageSchema,
    FunnelSnapshot,
    SessionCounters,
    SyncStatus,
} from '../models/analytics.interfaces';

@Injectable()
export class AnalyticsStorageService implements OnDestroy {
    private config: AnalyticsConfig = DEFAULT_ANALYTICS_CONFIG;
    private memoryBuffer: AnalyticsEvent[] = [];
    private writeScheduled = false;
    private storageAvailable = true;

    constructor() {
        this.checkStorageAvailability();
    }

    ngOnDestroy(): void {
        // Flush remaining buffer to storage on destroy
        this.flushToStorage();
    }

    // ───────────── Configuration ──────────────

    setConfig(config: Partial<AnalyticsConfig>): void {
        this.config = { ...this.config, ...config };
    }

    // ───────────── Storage Availability ──────────────

    private checkStorageAvailability(): void {
        try {
            const testKey = '__pm_analytics_test__';
            localStorage.setItem(testKey, '1');
            localStorage.removeItem(testKey);
            this.storageAvailable = true;
        } catch {
            this.storageAvailable = false;
            console.warn('[PM Analytics] localStorage unavailable – events will be kept in memory only');
        }
    }

    isStorageAvailable(): boolean {
        return this.storageAvailable;
    }

    // ───────────── Read ──────────────

    getStorageData(): AnalyticsStorageSchema {
        if (!this.storageAvailable) {
            return this.getDefaultSchema();
        }
        try {
            const raw = localStorage.getItem(this.storageKey());
            if (!raw) {
                return this.getDefaultSchema();
            }
            const parsed: AnalyticsStorageSchema = JSON.parse(raw);

            // Migrate schema if needed
            if (parsed.version < ANALYTICS_VERSION) {
                return this.migrateSchema(parsed);
            }
            return parsed;
        } catch (e) {
            console.error('[PM Analytics] Failed to read storage:', e);
            return this.getDefaultSchema();
        }
    }

    // ───────────── Write (batched via microtask) ──────────────

    addEvent(event: AnalyticsEvent): void {
        this.memoryBuffer.push(event);
        this.scheduleWrite();
    }

    addEvents(events: AnalyticsEvent[]): void {
        this.memoryBuffer.push(...events);
        this.scheduleWrite();
    }

    private scheduleWrite(): void {
        if (this.writeScheduled) return;
        this.writeScheduled = true;

        // Use queueMicrotask for zero-delay batching
        Promise.resolve().then(() => {
            this.flushToStorage();
            this.writeScheduled = false;
        });
    }

    private flushToStorage(): void {
        if (this.memoryBuffer.length === 0) return;

        const schema = this.getStorageData();
        schema.pendingEvents.push(...this.memoryBuffer);
        this.memoryBuffer = [];

        // Ring-buffer eviction: drop oldest pending events
        if (schema.pendingEvents.length > this.config.maxPendingEvents) {
            const overflow = schema.pendingEvents.length - this.config.maxPendingEvents;
            schema.pendingEvents.splice(0, overflow);
        }

        schema.lastWriteTimestamp = new Date().toISOString();
        this.writeSchema(schema);
    }

    // ───────────── Pending / Failed Event Management ──────────────

    getPendingEvents(batchSize?: number): AnalyticsEvent[] {
        // Flush memory buffer first
        this.flushToStorage();

        const schema = this.getStorageData();
        const size = batchSize || this.config.batchSize;
        return schema.pendingEvents.slice(0, size);
    }

    /**
     * Mark events as synced, removing them from pending.
     */
    markEventsSynced(eventIds: string[]): void {
        const schema = this.getStorageData();
        const idSet = new Set(eventIds);
        schema.pendingEvents = schema.pendingEvents.filter(
            (e) => !idSet.has(e.eventId)
        );
        schema.lastSyncTimestamp = new Date().toISOString();
        schema.consecutiveSyncFailures = 0;
        this.writeSchema(schema);
    }

    /**
     * Move events to the failed queue for retry.
     */
    markEventsFailed(events: AnalyticsEvent[]): void {
        const schema = this.getStorageData();

        // Remove from pending
        const failedIds = new Set(events.map((e) => e.eventId));
        schema.pendingEvents = schema.pendingEvents.filter(
            (e) => !failedIds.has(e.eventId)
        );

        // Add to failed (with ring-buffer eviction)
        for (const ev of events) {
            ev.syncStatus = SyncStatus.Failed;
        }
        schema.failedEvents.push(...events);
        if (schema.failedEvents.length > this.config.maxFailedEvents) {
            schema.failedEvents.splice(
                0,
                schema.failedEvents.length - this.config.maxFailedEvents
            );
        }

        schema.consecutiveSyncFailures++;
        this.writeSchema(schema);
    }

    /**
     * Move failed events back to pending for retry.
     */
    retryFailedEvents(): void {
        const schema = this.getStorageData();
        if (schema.failedEvents.length === 0) return;

        for (const ev of schema.failedEvents) {
            ev.syncStatus = SyncStatus.Pending;
        }
        schema.pendingEvents.push(...schema.failedEvents);
        schema.failedEvents = [];

        // Enforce max pending
        if (schema.pendingEvents.length > this.config.maxPendingEvents) {
            schema.pendingEvents.splice(
                0,
                schema.pendingEvents.length - this.config.maxPendingEvents
            );
        }
        this.writeSchema(schema);
    }

    hasPendingData(): boolean {
        const schema = this.getStorageData();
        return (
            schema.pendingEvents.length > 0 ||
            schema.failedEvents.length > 0 ||
            this.memoryBuffer.length > 0
        );
    }

    getConsecutiveFailures(): number {
        return this.getStorageData().consecutiveSyncFailures;
    }

    // ───────────── Session Counters ──────────────

    updateSessionCounters(
        updater: (counters: SessionCounters) => SessionCounters
    ): void {
        const schema = this.getStorageData();
        schema.sessionCounters = updater(schema.sessionCounters);
        this.writeSchema(schema);
    }

    getSessionCounters(): SessionCounters {
        return this.getStorageData().sessionCounters;
    }

    resetSessionCounters(): void {
        const schema = this.getStorageData();
        schema.sessionCounters = this.getDefaultCounters();
        this.writeSchema(schema);
    }

    // ───────────── Funnel Snapshots ──────────────

    saveFunnelSnapshot(key: string, snapshot: FunnelSnapshot): void {
        const schema = this.getStorageData();
        schema.funnelSnapshots[key] = snapshot;
        this.writeSchema(schema);
    }

    getFunnelSnapshot(key: string): FunnelSnapshot | undefined {
        return this.getStorageData().funnelSnapshots[key];
    }

    getAllFunnelSnapshots(): Record<string, FunnelSnapshot> {
        return this.getStorageData().funnelSnapshots;
    }

    clearFunnelSnapshots(): void {
        const schema = this.getStorageData();
        schema.funnelSnapshots = {};
        this.writeSchema(schema);
    }

    // ───────────── Session Management ──────────────

    setCurrentSessionId(sessionId: string): void {
        const schema = this.getStorageData();
        schema.currentSessionId = sessionId;
        this.writeSchema(schema);
    }

    getCurrentSessionId(): string {
        return this.getStorageData().currentSessionId;
    }

    // ───────────── Size Guard ──────────────

    getStorageSizeBytes(): number {
        const raw = localStorage.getItem(this.storageKey());
        return raw ? new Blob([raw]).size : 0;
    }

    isStorageNearLimit(): boolean {
        return this.getStorageSizeBytes() > this.config.maxStorageSizeBytes * 0.8;
    }

    /**
     * Emergency cleanup: drop oldest 25% of pending events.
     */
    emergencyCleanup(): void {
        const schema = this.getStorageData();
        const dropCount = Math.ceil(schema.pendingEvents.length * 0.25);
        schema.pendingEvents.splice(0, dropCount);
        schema.failedEvents = [];
        this.writeSchema(schema);
        console.warn(
            `[PM Analytics] Emergency cleanup: dropped ${dropCount} oldest events`
        );
    }

    // ───────────── Full Reset ──────────────

    clearAll(): void {
        this.memoryBuffer = [];
        if (this.storageAvailable) {
            localStorage.removeItem(this.storageKey());
        }
    }

    // ───────────── Internals ──────────────

    private storageKey(): string {
        return this.config.storageKeyPrefix || ANALYTICS_STORAGE_KEY;
    }

    private writeSchema(schema: AnalyticsStorageSchema): void {
        if (!this.storageAvailable) return;
        try {
            const serialized = JSON.stringify(schema);

            // Check max size
            if (new Blob([serialized]).size > this.config.maxStorageSizeBytes) {
                this.emergencyCleanup();
                return;
            }

            localStorage.setItem(this.storageKey(), serialized);
        } catch (e) {
            // Likely QuotaExceededError
            console.error('[PM Analytics] Storage write failed:', e);
            this.emergencyCleanup();
        }
    }

    private getDefaultSchema(): AnalyticsStorageSchema {
        return {
            version: ANALYTICS_VERSION,
            currentSessionId: '',
            lastSyncTimestamp: '',
            lastWriteTimestamp: '',
            consecutiveSyncFailures: 0,
            pendingEvents: [],
            failedEvents: [],
            sessionCounters: this.getDefaultCounters(),
            funnelSnapshots: {},
        };
    }

    private getDefaultCounters(): SessionCounters {
        return {
            searchCount: 0,
            cartAddCount: 0,
            cartRemoveCount: 0,
            cartModificationCount: 0,
            checkoutClickCount: 0,
            proceedToBuyCount: 0,
            proceedToBuySuccess: 0,
            poGeneratedCount: 0,
            pageViewCount: 0,
        };
    }

    /**
     * Forward-compatible migration. Currently no-op for version 1.
     */
    private migrateSchema(old: AnalyticsStorageSchema): AnalyticsStorageSchema {
        // Future: add migration logic per version step
        old.version = ANALYTICS_VERSION;
        this.writeSchema(old);
        return old;
    }
}
